import type { AIAnalysisResult } from '@/types';
import { postWithAuth } from './ensureSession';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
// Фото/текст-анализ еды. По умолчанию — функция analyze-meal (как в Telegram-версии),
// чтобы не зависеть от отдельной переменной VITE_AI_ENDPOINT (её на веб-деплое не было →
// анализ еды падал «VITE_AI_ENDPOINT не настроен»).
const ENDPOINT = import.meta.env.VITE_AI_ENDPOINT || `${SUPABASE_URL}/functions/v1/analyze-meal`;

function normalizeComponents(raw: any) {
  if (!Array.isArray(raw)) return [];

  return raw
    .slice(0, 15)
    .map((c: any) => ({
      name: String(c?.name ?? 'Компонент').slice(0, 80),
      weight_g: Math.max(0, Math.round(Number(c?.weight_g ?? c?.weight ?? 0) || 0)),
      calories: Math.max(0, Math.round(Number(c?.calories ?? c?.kcal ?? 0) || 0)),
    }))
    .filter((c: any) => c.name && c.name !== 'Компонент');
}

function mapAIItem(i: any) {
  return {
    name: String(i.name ?? 'Продукт'),
    estimated_weight_g: Number(i.estimated_weight_g) || 100,
    calories: Number(i.calories) || 0,
    protein_g: Number(i.protein_g) || 0,
    fat_g: Number(i.fat_g) || 0,
    carbs_g: Number(i.carbs_g) || 0,

    // Микронутриенты
    fiber_g: Number(i.fiber_g) || 0,
    vegetables_fruits_g: Number(i.vegetables_fruits_g) || 0,
    omega3_g: Number(i.omega3_g) || 0,
    saturated_fat_g: Number(i.saturated_fat_g) || 0,
    calcium_mg: Number(i.calcium_mg) || 0,
    iron_mg: Number(i.iron_mg) || 0,
    magnesium_mg: Number(i.magnesium_mg) || 0,
    zinc_mg: Number(i.zinc_mg) || 0,
    potassium_mg: Number(i.potassium_mg) || 0,
    vitamin_d_mcg: Number(i.vitamin_d_mcg) || 0,
    vitamin_b12_mcg: Number(i.vitamin_b12_mcg) || 0,
    vitamin_c_mg: Number(i.vitamin_c_mg) || 0,

    // Состав для сложных блюд: роллы, шаурма, суши, бургер, пицца и т.д.
    components: normalizeComponents(i.components),
  };
}

async function callAI(payload: unknown): Promise<AIAnalysisResult> {
  if (!ENDPOINT) throw new Error('VITE_AI_ENDPOINT не настроен');

  const res = await postWithAuth(ENDPOINT, payload);

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`AI error ${res.status}: ${text}`);
  }

  const data = await res.json();

  if (!Array.isArray(data.items)) {
    throw new Error('Неверный формат ответа от AI');
  }

  return {
    items: data.items.map(mapAIItem),
    confidence: Number(data.confidence) || 0.5,
    notes: data.notes,
  };
}

/** Анализ фото блюда */
export async function analyzePhoto(imageBase64: string): Promise<AIAnalysisResult> {
  return callAI({ mode: 'photo', image: imageBase64 });
}

/** Парсинг свободного текста */
export async function analyzeText(text: string): Promise<AIAnalysisResult> {
  return callAI({ mode: 'text', text });
}

// ============================================================
// НОВЫЕ ФУНКЦИИ
// ============================================================

/** Результат распознавания этикетки */
export interface LabelAnalysisResult {
  found: boolean;
  product_name?: string;
  per_grams?: number;      // на сколько грамм указаны значения (обычно 100)
  calories?: number;
  protein_g?: number;
  fat_g?: number;
  carbs_g?: number;
}

/**
 * Анализ этикетки с пищевой ценностью.
 * Возвращает КБЖУ "на 100г" или на порцию — фронт пересчитывает по введённой граммовке.
 */
export async function analyzeLabel(imageBase64: string): Promise<LabelAnalysisResult> {
  if (!SUPABASE_URL) throw new Error('VITE_SUPABASE_URL не настроен');

  const res = await postWithAuth(`${SUPABASE_URL}/functions/v1/analyze-label`, { image: imageBase64 });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Label AI error ${res.status}: ${text}`);
  }

  const data = await res.json();

  return {
    found: !!data.found,
    product_name: data.product_name,
    per_grams: Number(data.per_grams) || 100,
    calories: Number(data.calories) || 0,
    protein_g: Number(data.protein_g) || 0,
    fat_g: Number(data.fat_g) || 0,
    carbs_g: Number(data.carbs_g) || 0,
  };
}

/**
 * Анализ рукописного/печатного списка продуктов.
 * Возвращает массив продуктов с КБЖУ — как обычный AIAnalysisResult.
 */
export async function analyzeHandwritten(imageBase64: string): Promise<AIAnalysisResult> {
  if (!SUPABASE_URL) throw new Error('VITE_SUPABASE_URL не настроен');

  const res = await postWithAuth(`${SUPABASE_URL}/functions/v1/analyze-handwritten`, { image: imageBase64 });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Handwritten AI error ${res.status}: ${text}`);
  }

  const data = await res.json();

  if (!Array.isArray(data.items)) {
    return { items: [], confidence: 0 };
  }

  return {
    items: data.items.map(mapAIItem),
    confidence: Number(data.confidence) || 0.8,
    notes: data.notes,
  };
}

/** Конвертация File → base64 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();

    r.onload = () => {
      const result = r.result as string;
      resolve(result.split(',')[1]);
    };

    r.onerror = () => reject(new Error('Не удалось прочитать файл'));
    r.readAsDataURL(file);
  });
}
